// src/pages/AdminFinancePage.jsx
// Bloque 6 — Módulo financiero FIXO
// ============================================================

import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "../api/api";

const font = "Montserrat, sans-serif";

function formatARS(n) {
  if (!n || isNaN(n)) return "$0";
  return new Intl.NumberFormat("es-AR", {
    style: "currency", currency: "ARS", maximumFractionDigits: 0,
  }).format(Number(n));
}

function timeAgo(dateStr) {
  if (!dateStr) return "—";
  const diff = (Date.now() - new Date(dateStr)) / 1000;
  if (diff < 60)    return "hace un momento";
  if (diff < 3600)  return `hace ${Math.floor(diff / 60)} min`;
  if (diff < 86400) return `hace ${Math.floor(diff / 3600)}h`;
  return new Date(dateStr).toLocaleDateString("es-AR");
}

const STATUS_MAP = {
  pending:   { label: "Pendiente",  color: "#b06a00", bg: "rgba(176,106,0,0.1)" },
  paid:      { label: "Pagado",     color: "#1b7f4e", bg: "rgba(27,127,78,0.1)" },
  completed: { label: "Completado", color: "#163244", bg: "rgba(22,50,68,0.08)" },
  cancelled: { label: "Cancelado",  color: "#c0392b", bg: "rgba(192,57,43,0.1)" },
  refunded:  { label: "Reembolsado",color: "#7b5ea7", bg: "rgba(123,94,167,0.1)" },
};

const TIER_MAP = {
  new:    { label: "Sin score", color: "#7a93a4" },
  bronze: { label: "Bronce",   color: "#b06a00" },
  silver: { label: "Plata",    color: "#7b5ea7" },
  gold:   { label: "Oro",      color: "#E7A432" },
};

// ─── Metric card ─────────────────────────────────────────────────────────────
function MetricCard({ icon, label, value, sub, color = "#163244" }) {
  return (
    <div style={{ background: "#fff", borderRadius: 14, border: "1px solid rgba(0,0,0,0.07)", padding: "16px 18px" }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
        <span style={{ fontSize: 10, fontWeight: 700, color: "#7a93a4", textTransform: "uppercase", letterSpacing: "0.07em" }}>{label}</span>
        <span style={{ fontSize: 18 }}>{icon}</span>
      </div>
      <div style={{ fontSize: 24, fontWeight: 900, color, lineHeight: 1.1 }}>{value}</div>
      {sub && <div style={{ fontSize: 11, color: "#aab8c2", marginTop: 4 }}>{sub}</div>}
    </div>
  );
}

// ─── Payout modal ─────────────────────────────────────────────────────────────
function PayoutModal({ transaction, onClose, onSuccess }) {
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState("");

  const handlePayout = async () => {
    setLoading(true);
    setError("");
    try {
      await api.post(`/payments/payout/${transaction.id}`);
      onSuccess();
    } catch (err) {
      setError(err.response?.data?.error || "Error al ejecutar el payout.");
      setLoading(false);
    }
  };

  return (
    <div style={{
      position: "fixed", inset: 0, zIndex: 200,
      background: "rgba(22,50,68,0.55)", backdropFilter: "blur(3px)",
      display: "flex", alignItems: "center", justifyContent: "center", padding: 20,
    }}>
      <div style={{ background: "#fff", borderRadius: 18, padding: "32px 28px", width: "min(420px, 100%)", boxShadow: "0 16px 60px rgba(22,50,68,0.22)" }}>
        <div style={{ textAlign: "center", marginBottom: 24 }}>
          <div style={{ width: 64, height: 64, borderRadius: "50%", margin: "0 auto 18px", background: "rgba(27,127,78,0.1)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 28 }}>💸</div>
          <h3 style={{ margin: "0 0 8px", color: "#163244", fontSize: 17, fontWeight: 800, fontFamily: font }}>Ejecutar liquidación</h3>
          <p style={{ margin: 0, color: "#7a93a4", fontSize: 13, lineHeight: 1.6, fontFamily: font }}>
            Se liquidará <strong style={{ color: "#163244" }}>{formatARS(transaction.net_amount)}</strong> al prestador <strong style={{ color: "#163244" }}>{transaction.provider_name}</strong>.
          </p>
        </div>
        {error && (
          <div style={{ background: "rgba(192,57,43,0.08)", border: "1px solid rgba(192,57,43,0.2)", borderRadius: 8, padding: "10px 14px", marginBottom: 16, fontSize: 13, color: "#c0392b", fontFamily: font }}>
            {error}
          </div>
        )}
        <div style={{ display: "flex", gap: 10 }}>
          <button onClick={onClose} disabled={loading} style={{ flex: 1, padding: "12px", borderRadius: 50, border: "2px solid rgba(0,0,0,0.1)", background: "transparent", color: "#7a93a4", fontFamily: font, fontWeight: 600, fontSize: 14, cursor: "pointer" }}>
            Cancelar
          </button>
          <button onClick={handlePayout} disabled={loading} style={{ flex: 2, padding: "12px", borderRadius: 50, border: "none", background: loading ? "#dce5ec" : "#163244", color: "#fff", fontFamily: font, fontWeight: 700, fontSize: 14, cursor: loading ? "not-allowed" : "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 6 }}>
            {loading
              ? <><span style={{ width: 14, height: 14, border: "2px solid #fff", borderTopColor: "transparent", borderRadius: "50%", animation: "spin 0.7s linear infinite", display: "inline-block" }} /> Procesando...</>
              : "Confirmar liquidación"
            }
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Main ─────────────────────────────────────────────────────────────────────
export default function AdminFinancePage() {
  const navigate = useNavigate();

  const [stats,        setStats]        = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [totals,       setTotals]       = useState(null);
  const [loading,      setLoading]      = useState(true);
  const [loadingTx,    setLoadingTx]    = useState(false);
  const [error,        setError]        = useState("");
  const [payoutModal,  setPayoutModal]  = useState(null);
  const [toast,        setToast]        = useState(null);

  // Filtros tabla
  const [filterStatus, setFilterStatus] = useState("");
  const [offset,       setOffset]       = useState(0);
  const LIMIT = 20;

  // Filtros CSV
  const [csvDesde,    setCsvDesde]    = useState("");
  const [csvHasta,    setCsvHasta]    = useState("");
  const [exportando,  setExportando]  = useState(false);

  const showToast = (msg) => { setToast(msg); setTimeout(() => setToast(null), 3500); };

  const fetchStats = useCallback(async () => {
    try {
      const res = await api.get("/payments/stats");
      setStats(res.data);
    } catch { /* silencioso */ }
  }, []);

  const fetchTransactions = useCallback(async (reset = false) => {
    setLoadingTx(true);
    try {
      const params = { limit: LIMIT, offset: reset ? 0 : offset };
      if (filterStatus) params.status = filterStatus;
      const res = await api.get("/payments/history", { params });
      setTransactions(res.data.transactions || []);
      setTotals(res.data.totals || null);
      if (reset) setOffset(0);
    } catch (err) {
      setError(err.response?.data?.error || "Error al cargar transacciones.");
    } finally {
      setLoadingTx(false);
    }
  }, [filterStatus, offset]);

  useEffect(() => {
    setLoading(true);
    Promise.all([fetchStats(), fetchTransactions(true)]).finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    fetchTransactions(true);
  }, [filterStatus]);

  const handlePayoutSuccess = () => {
    setPayoutModal(null);
    showToast("Liquidación iniciada correctamente.");
    fetchStats();
    fetchTransactions(true);
  };

  const handleExportCSV = async () => {
    setExportando(true);
    try {
      const params = new URLSearchParams();
      if (csvDesde) params.append("desde", csvDesde);
      if (csvHasta) params.append("hasta", csvHasta);

      const token = localStorage.getItem("token");
      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/payments/admin/transactions/export?${params}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (!res.ok) throw new Error("Error al exportar");

      const blob = await res.blob();
      const url  = URL.createObjectURL(blob);
      const a    = document.createElement("a");
      a.href     = url;
      a.download = `fixo_transacciones_${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      showToast("CSV exportado correctamente.");
    } catch {
      showToast("No se pudo exportar el CSV. Intentá de nuevo.");
    } finally {
      setExportando(false);
    }
  };

  if (loading) return (
    <div style={{ minHeight: "60vh", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", fontFamily: font, gap: 12 }}>
      <span style={{ width: 32, height: 32, border: "3px solid #dce5ec", borderTopColor: "#163244", borderRadius: "50%", animation: "spin 0.7s linear infinite", display: "inline-block" }} />
      <div style={{ fontSize: 13, color: "#7a93a4", fontWeight: 600 }}>Cargando módulo financiero…</div>
      <style>{"@keyframes spin{to{transform:rotate(360deg)}}"}</style>
    </div>
  );

  const fin = stats || {};

  return (
    <>
      <div style={{ fontFamily: font, background: "#f5f7fa", minHeight: "100vh", padding: "24px 24px 60px" }}>
        <div style={{ maxWidth: 1280, margin: "0 auto" }}>

          {/* Header */}
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 24, flexWrap: "wrap", gap: 12 }}>
            <div>
              <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 4 }}>
                <button onClick={() => navigate("/admin/dashboard")} style={{ background: "none", border: "none", cursor: "pointer", color: "#7a93a4", fontFamily: font, fontSize: 13, fontWeight: 500, padding: 0, display: "flex", alignItems: "center", gap: 5 }}>
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M15 18l-6-6 6-6"/></svg>
                  Dashboard
                </button>
                <span style={{ color: "#dce5ec" }}>/</span>
                <span style={{ color: "#163244", fontWeight: 600, fontSize: 13 }}>Finanzas</span>
              </div>
              <h1 style={{ margin: 0, fontSize: 22, fontWeight: 800, color: "#163244" }}>Módulo financiero</h1>
            </div>
            <button onClick={() => { fetchStats(); fetchTransactions(true); }} style={{ padding: "9px 20px", borderRadius: 50, border: "1.5px solid rgba(22,50,68,0.2)", background: "#fff", color: "#163244", fontFamily: font, fontWeight: 700, fontSize: 12, cursor: "pointer" }}>
              ↻ Actualizar
            </button>
          </div>

          {/* Métricas */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 12, marginBottom: 24 }}>
            <MetricCard icon="💳" label="Volumen total"            value={formatARS(fin.total_volume)}    color="#163244" sub={`${fin.completed_count || 0} pagos completados`} />
            <MetricCard icon="📈" label="Ingresos FIXO"            value={formatARS(fin.fixo_income)}     color="#1b7f4e" sub="Comisiones retenidas" />
            <MetricCard icon="⏳" label="Liquidaciones pendientes" value={formatARS(fin.pending_payouts)} color="#b06a00" sub={`${fin.pending_payouts_count || 0} prestadores esperando`} />
            <MetricCard icon="📅" label="Pagos hoy"                value={formatARS(fin.today_volume)}    color="#163244" sub="Volumen del día" />
          </div>

          {/* Tabla de transacciones */}
          <div style={{ background: "#fff", borderRadius: 16, border: "1px solid rgba(0,0,0,0.07)", overflow: "hidden" }}>

            {/* Header tabla */}
            <div style={{ padding: "16px 20px", borderBottom: "1px solid rgba(0,0,0,0.06)" }}>

              {/* Fila superior: título + filtro estado */}
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 12, marginBottom: 12 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <div style={{ width: 3, height: 14, background: "#E7A432", borderRadius: 2 }} />
                  <span style={{ fontWeight: 700, fontSize: 13, color: "#163244" }}>💳 Historial de transacciones</span>
                  {totals && <span style={{ fontSize: 11, color: "#7a93a4", marginLeft: 4 }}>— {transactions.length} registros</span>}
                </div>
                <select
                  value={filterStatus}
                  onChange={e => setFilterStatus(e.target.value)}
                  style={{ padding: "7px 14px", borderRadius: 50, border: "1.5px solid rgba(22,50,68,0.15)", fontFamily: font, fontSize: 11, fontWeight: 600, color: "#163244", background: "#f5f7fa", outline: "none", cursor: "pointer" }}
                >
                  <option value="">Todos los estados</option>
                  <option value="pending">Pendiente</option>
                  <option value="paid">Pagado</option>
                  <option value="completed">Completado</option>
                  <option value="cancelled">Cancelado</option>
                </select>
              </div>

              {/* Fila inferior: filtros CSV + botón exportar */}
              <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                <span style={{ fontSize: 11, fontWeight: 600, color: "#7a93a4" }}>Exportar CSV:</span>
                <input
                  type="date"
                  value={csvDesde}
                  onChange={e => setCsvDesde(e.target.value)}
                  style={{ fontFamily: font, fontSize: 12, padding: "6px 10px", borderRadius: 8, border: "1.5px solid rgba(22,50,68,0.15)", color: "#163244", outline: "none" }}
                />
                <span style={{ fontSize: 12, color: "#aab8c2" }}>→</span>
                <input
                  type="date"
                  value={csvHasta}
                  onChange={e => setCsvHasta(e.target.value)}
                  style={{ fontFamily: font, fontSize: 12, padding: "6px 10px", borderRadius: 8, border: "1.5px solid rgba(22,50,68,0.15)", color: "#163244", outline: "none" }}
                />
                <button
                  onClick={handleExportCSV}
                  disabled={exportando}
                  style={{
                    fontFamily: font, fontWeight: 700, fontSize: 12,
                    padding: "7px 18px", borderRadius: 50,
                    background: exportando ? "#dce5ec" : "#163244",
                    color: exportando ? "#7a93a4" : "#fff",
                    border: "none", cursor: exportando ? "not-allowed" : "pointer",
                    display: "flex", alignItems: "center", gap: 6,
                    transition: "background 0.2s",
                  }}
                >
                  {exportando
                    ? <><span style={{ width: 12, height: 12, border: "2px solid #7a93a4", borderTopColor: "transparent", borderRadius: "50%", animation: "spin 0.7s linear infinite", display: "inline-block" }} /> Generando...</>
                    : "⬇ Exportar CSV"
                  }
                </button>
                {(csvDesde || csvHasta) && (
                  <button
                    onClick={() => { setCsvDesde(""); setCsvHasta(""); }}
                    style={{ fontSize: 11, color: "#7a93a4", background: "none", border: "none", cursor: "pointer", fontFamily: font, padding: 0 }}
                  >
                    ✕ limpiar
                  </button>
                )}
              </div>

            </div>

            {/* Loading overlay */}
            {loadingTx && (
              <div style={{ padding: "32px", textAlign: "center", color: "#7a93a4", fontSize: 13 }}>
                <span style={{ display: "inline-block", width: 20, height: 20, border: "2px solid #dce5ec", borderTopColor: "#163244", borderRadius: "50%", animation: "spin 0.7s linear infinite", marginRight: 8, verticalAlign: "middle" }} />
                Cargando...
              </div>
            )}

            {/* Sin datos */}
            {!loadingTx && transactions.length === 0 && (
              <div style={{ padding: "48px 20px", textAlign: "center", color: "#aab8c2" }}>
                <div style={{ fontSize: 36, marginBottom: 12 }}>💳</div>
                <div style={{ fontWeight: 700, fontSize: 15, color: "#163244", marginBottom: 6 }}>Sin transacciones aún</div>
                <div style={{ fontSize: 13 }}>Las transacciones aparecerán aquí cuando los clientes completen pagos.</div>
              </div>
            )}

            {/* Filas */}
            {!loadingTx && transactions.length > 0 && (
              <div style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
                  <thead>
                    <tr style={{ background: "#f8fafb" }}>
                      {["ID", "Cliente", "Prestador", "Tier", "Monto", "Comisión FIXO", "Neto prestador", "Estado", "Fecha", "Acción"].map(h => (
                        <th key={h} style={{ padding: "10px 14px", textAlign: "left", fontWeight: 700, color: "#7a93a4", fontSize: 10, textTransform: "uppercase", letterSpacing: "0.05em", borderBottom: "1px solid rgba(0,0,0,0.06)", whiteSpace: "nowrap" }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {transactions.map((tx, i) => {
                      const st   = STATUS_MAP[tx.status]   || STATUS_MAP.pending;
                      const tier = TIER_MAP[tx.provider_tier] || TIER_MAP.new;
                      return (
                        <tr key={tx.id} style={{ borderBottom: "1px solid rgba(0,0,0,0.04)", background: i % 2 === 0 ? "#fff" : "#fafbfc" }}>
                          <td style={{ padding: "11px 14px", color: "#163244", fontWeight: 700 }}>#{tx.id}</td>
                          <td style={{ padding: "11px 14px" }}>
                            <div style={{ fontWeight: 600, color: "#163244" }}>{tx.client_name}</div>
                            <div style={{ fontSize: 10, color: "#aab8c2" }}>{tx.client_email}</div>
                          </td>
                          <td style={{ padding: "11px 14px" }}>
                            <div style={{ fontWeight: 600, color: "#163244" }}>{tx.provider_name}</div>
                            <div style={{ fontSize: 10, color: "#aab8c2" }}>{tx.service_description?.slice(0, 30)}</div>
                          </td>
                          <td style={{ padding: "11px 14px" }}>
                            <span style={{ fontSize: 10, fontWeight: 700, color: tier.color, background: `${tier.color}18`, padding: "2px 8px", borderRadius: 10 }}>
                              {tier.label}
                            </span>
                          </td>
                          <td style={{ padding: "11px 14px", fontWeight: 800, color: "#163244" }}>{formatARS(tx.amount)}</td>
                          <td style={{ padding: "11px 14px", fontWeight: 700, color: "#1b7f4e" }}>
                            {formatARS(tx.commission_amount)}
                            <div style={{ fontSize: 10, color: "#aab8c2" }}>{Math.round(tx.commission_rate * 100)}%</div>
                          </td>
                          <td style={{ padding: "11px 14px", fontWeight: 700, color: "#163244" }}>{formatARS(tx.net_amount)}</td>
                          <td style={{ padding: "11px 14px" }}>
                            <span style={{ fontSize: 10, fontWeight: 700, color: st.color, background: st.bg, padding: "3px 10px", borderRadius: 10 }}>
                              {st.label}
                            </span>
                          </td>
                          <td style={{ padding: "11px 14px", color: "#7a93a4", fontSize: 11, whiteSpace: "nowrap" }}>
                            {timeAgo(tx.paid_at || tx.created_at)}
                          </td>
                          <td style={{ padding: "11px 14px" }}>
                            {tx.status === "paid" && (
                              <button
                                onClick={() => setPayoutModal(tx)}
                                style={{ padding: "6px 14px", borderRadius: 50, border: "none", background: "#163244", color: "#fff", fontFamily: font, fontWeight: 700, fontSize: 11, cursor: "pointer", whiteSpace: "nowrap" }}
                              >
                                Liquidar →
                              </button>
                            )}
                            {tx.status === "completed" && (
                              <span style={{ fontSize: 10, color: "#1b7f4e", fontWeight: 700 }}>✓ Liquidado</span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            {/* Paginación */}
            {transactions.length === LIMIT && (
              <div style={{ padding: "14px 20px", borderTop: "1px solid rgba(0,0,0,0.06)", display: "flex", justifyContent: "center" }}>
                <button
                  onClick={() => { setOffset(o => o + LIMIT); fetchTransactions(); }}
                  style={{ padding: "9px 24px", borderRadius: 50, border: "1.5px solid rgba(22,50,68,0.2)", background: "#fff", color: "#163244", fontFamily: font, fontWeight: 700, fontSize: 12, cursor: "pointer" }}
                >
                  Cargar más
                </button>
              </div>
            )}
          </div>

        </div>
      </div>

      {/* Payout modal */}
      {payoutModal && (
        <PayoutModal
          transaction={payoutModal}
          onClose={() => setPayoutModal(null)}
          onSuccess={handlePayoutSuccess}
        />
      )}

      {/* Toast */}
      {toast && (
        <div style={{ position: "fixed", bottom: 24, left: "50%", transform: "translateX(-50%)", background: "#163244", color: "#fff", padding: "13px 26px", borderRadius: 10, fontWeight: 600, fontSize: 14, zIndex: 999, boxShadow: "0 4px 20px rgba(0,0,0,0.2)", borderLeft: "4px solid #E7A432", whiteSpace: "nowrap", fontFamily: font }}>
          {toast}
        </div>
      )}

      <style>{`
        * { box-sizing: border-box; }
        @keyframes spin { to { transform: rotate(360deg); } }
      `}</style>
    </>
  );
}
